import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Download, Trash2, FileText, Loader2, Search, ScrollText, PenLine, Send, Pencil, CheckCircle2, BookOpen, ShieldCheck, ExternalLink } from "lucide-react";
import { getLeases, getTenants, createLease, updateLease, deleteLease, downloadLeasePdf, generateLeaseRiskReport, sendLeaseForSignature, sendLeaseToNotion } from "@/lib/api";
import { toast } from "sonner";

// ── Config bailleur par structure (pré-remplissage automatique) ───────────────
const SCI_CONFIG = {
  "lambersart": {
    bailleur_sci:     "BH MED",
    bailleur_forme:   "Société par actions simplifiée (SAS)",
    bailleur_siege:   "60 rue François Ier, 75008 Paris",
    bailleur_gerants: "M. Gaëtan BOONE, président, et M. Clément HATTE, directeur général",
    bailleur_capital: "200",
    bailleur_rcs:     "Paris",
    bailleur_rcs_numero: "999 671 316",
    local_adresse_defaut: "7 rue Robert Transon, 59130 Lambersart",
    lieu_signature_defaut: "Lambersart",
  },
  "seclin": {
    bailleur_sci:     "SCI C2GR",
    bailleur_forme:   "Société civile immobilière (SCI)",
    bailleur_siege:   "15 rue Maurice Bouchery, 59113 Seclin",
    bailleur_gerants: "M. Clément HENNEBELLE et M. Romain MARTIN",
    bailleur_capital: "",
    bailleur_rcs:     "",
    bailleur_rcs_numero: "",
    local_adresse_defaut: "15 rue Maurice Bouchery, 59113 Seclin",
  },
  "armentieres": {
    bailleur_sci:     "",
    bailleur_forme:   "Société civile immobilière (SCI)",
    bailleur_siege:   "1 rue de Strasbourg, 59280 Armentières",
    bailleur_gerants: "",
    bailleur_capital: "",
    bailleur_rcs:     "",
    bailleur_rcs_numero: "",
    local_adresse_defaut: "1 rue de Strasbourg, 59280 Armentières",
  },
  "hippocrate": {
    bailleur_sci:     "SCI Hippocrate",
    bailleur_forme:   "Société civile immobilière (SCI)",
    bailleur_siege:   "1 allée des Cordiers, 59910 Bondues",
    bailleur_gerants: "M. Gaëtan BOONE, M. Clément HATTE et Mme MARIEN, gérants",
    bailleur_capital: "300",
    bailleur_rcs:     "Lille Métropole",
    bailleur_rcs_numero: "993 463 892",
    local_adresse_defaut: "",
  },
  "la_madeleine": {
    bailleur_sci:     "SCI Hippocrate",
    bailleur_forme:   "Société civile immobilière (SCI)",
    bailleur_siege:   "1 allée des Cordiers, 59910 Bondues",
    bailleur_gerants: "M. Gaëtan BOONE, M. Clément HATTE et Mme MARIEN, gérants",
    bailleur_capital: "300",
    bailleur_rcs:     "Lille Métropole",
    bailleur_rcs_numero: "993 463 892",
    local_adresse_defaut: "184 bis rue du Général-de-Gaulle, 59110 La Madeleine",
    local_bureau_num_defaut: "A4 et A6",
    local_parties_semi_communes_defaut: "salle d'attente et sanitaires",
    lieu_signature_defaut: "La Madeleine",
    cadastre_section_defaut: "AD",
    cadastre_numero_defaut: "542",
    date_diagnostic_amiante_defaut: "2002-06-04",
    societe_diagnostic_defaut: "REACOPRO",
  },
};

// Variantes historiques utilisées dans les anciennes fiches locataires/baux.
const SCI_ALIASES = {
  lambersart: ["lambersart", "sci bhmed", "sas bhmed", "sas bh med"],
  seclin: ["seclin", "sci c2gr", "c2gr"],
  armentieres: ["armentieres", "1 rue de strasbourg"],
  la_madeleine: ["la madeleine", "lamadeleine", "184 bis rue du general de gaulle"],
  hippocrate: ["hippocrate", "sci hippocrate"],
};

// Normalise une chaîne pour comparaison (minuscules, sans accents ni espaces)
function normalizeStr(str) {
  return str.toLowerCase()
    .replace(/[éèêë]/g, "e").replace(/[àâä]/g, "a").replace(/[ùûü]/g, "u")
    .replace(/[îï]/g, "i").replace(/[ôö]/g, "o").replace(/[^a-z]/g, "");
}

// Retourne la config SCI correspondant à la structure du locataire
function getSciConfig(structure) {
  if (!structure) return null;
  const s = normalizeStr(structure);
  for (const [key, aliases] of Object.entries(SCI_ALIASES)) {
    if (aliases.some(alias => {
      const normalizedAlias = normalizeStr(alias);
      return s.includes(normalizedAlias) || normalizedAlias.includes(s);
    })) {
      return SCI_CONFIG[key];
    }
  }
  return null;
}

// ── Pad de signature tactile ──────────────────────────────────────────────────
function SignaturePad({ label, value, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return {
      x: (touch.clientX - rect.left) * (canvas.width / rect.width),
      y: (touch.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDraw = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const draw = (e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#1a1a2e";
    const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke();
  };
  const stopDraw = () => {
    if (!drawing.current) return; drawing.current = false;
    const canvas = canvasRef.current;
    const data = canvas.getContext("2d").getImageData(0, 0, canvas.width, canvas.height).data;
    onChange(data.some(v => v !== 0) ? canvas.toDataURL("image/png") : "");
  };
  const clear = () => { canvasRef.current.getContext("2d").clearRect(0, 0, 500, 160); onChange(""); };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-gray-700">{label}</Label>
        <button type="button" onClick={clear} className="text-xs text-gray-400 hover:text-red-500 transition-colors">Effacer</button>
      </div>
      <div className={`border-2 rounded-xl bg-white relative overflow-hidden transition-colors ${value ? "border-green-400" : "border-dashed border-gray-300"}`}>
        <canvas
          ref={canvasRef} width={500} height={160}
          className="w-full touch-none cursor-crosshair" style={{ display: "block" }}
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
        />
        {!value && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><span className="text-gray-300 text-sm select-none">Signez ici</span></div>}
      </div>
      {value && <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Signature enregistrée</p>}
    </div>
  );
}

const EMPTY_FORM = {
  tenant_id: "",
  structure: "",
  // Bailleur
  bailleur_sci: "",
  bailleur_forme: "",
  bailleur_siege: "",
  bailleur_gerants: "",
  bailleur_capital: "",
  bailleur_rcs: "",
  bailleur_rcs_numero: "",
  bailleur_contact: "",
  // Locataire
  locataire_civilite: "Docteur",
  locataire_nom: "",
  locataire_profession: "",
  locataire_adresse: "",
  locataire_date_naissance: "",
  locataire_lieu_naissance: "",
  locataire_rpps: "",
  locataire_ordre: "",
  // Local
  local_adresse: "",
  local_etage: "rez-de-chaussée",
  local_bureau_num: "",
  local_surface: "",
  local_surface_salle_attente: "",
  local_sdo: "",
  local_surface_totale_preneur: "",
  local_parties_semi_communes: "",
  local_acces: "",
  cadastre_section: "",
  cadastre_numero: "",
  cadastre_contenance: "",
  // Durée
  duree_ans: "6",
  duree_preavis_mois: "6",
  date_debut: "",
  // Loyer
  loyer_montant: "",
  loyer_revision_ilat: true,
  loyer_echeance_jour: "5",
  depot_garantie: "",
  provision_charges: "",
  // Charges
  charges_type: "provision",
  // Activité
  activite: "Consultations médicales",
  collaboration_autorisee: true,
  sous_location_autorisee: false,
  // Diagnostics
  date_diagnostic_amiante: "",
  societe_diagnostic: "",
  societe_dpe: "",
  date_dpe: "",
  // Signature
  lieu_signature: "",
  date_signature: "",
  notes: "",
};

export default function Leases() {
  const [leases, setLeases] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [generatingRiskId, setGeneratingRiskId] = useState(null);
  const [sendingNotionId, setSendingNotionId] = useState(null);
  const [signatureModal, setSignatureModal] = useState(false);
  const [signatureLease, setSignatureLease] = useState(null);
  const [signatureEmails, setSignatureEmails] = useState({ bailleur_email: "", preneur_email: "" });
  const [sendingSignature, setSendingSignature] = useState(false);
  const [signatureLinks, setSignatureLinks] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [freeTextMode, setFreeTextMode] = useState(false);
  const [signDirectDialog, setSignDirectDialog] = useState({ open: false, lease: null });
  const [signDirectSigs, setSignDirectSigs] = useState({ bailleur: "", locataire: "" });
  const [signDirectLoading, setSignDirectLoading] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editMode, setEditMode] = useState(false);
  const [editLeaseId, setEditLeaseId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [lRes, tRes] = await Promise.all([getLeases(), getTenants()]);
      setLeases(lRes.data);
      setTenants(tRes.data);
    } catch {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleTenantChange = (tenantId) => {
    const t = tenants.find(t => t.id === tenantId || t._id === tenantId);
    if (!t) return setFormData(f => ({ ...f, tenant_id: tenantId }));
    const sci = getSciConfig(t.structure);
    setFormData(f => ({
      ...f,
      tenant_id: tenantId,
      locataire_nom: t.name || f.locataire_nom,
      locataire_civilite: t.civilite || f.locataire_civilite,
      locataire_profession: t.profession || f.locataire_profession,
      locataire_adresse: t.property_address || f.locataire_adresse,
      loyer_montant: t.rent_amount ? String(t.rent_amount) : f.loyer_montant,
      depot_garantie: t.depot_garantie_notion ? String(t.depot_garantie_notion) : f.depot_garantie,
      provision_charges: t.charges_provisionnees ? String(t.charges_provisionnees) : f.provision_charges,
      local_adresse: t.bureau_local || t.property_address || (sci?.local_adresse_defaut) || f.local_adresse,
      local_bureau_num: sci?.local_bureau_num_defaut || f.local_bureau_num,
      local_parties_semi_communes: sci?.local_parties_semi_communes_defaut || f.local_parties_semi_communes,
      cadastre_section: sci?.cadastre_section_defaut || f.cadastre_section,
      cadastre_numero: sci?.cadastre_numero_defaut || f.cadastre_numero,
      date_diagnostic_amiante: sci?.date_diagnostic_amiante_defaut || f.date_diagnostic_amiante,
      societe_diagnostic: sci?.societe_diagnostic_defaut || f.societe_diagnostic,
      local_surface: t.surface ? String(t.surface) : f.local_surface,
      date_debut: t.date_entree ? t.date_entree.substring(0, 10) : f.date_debut,
      structure: t.structure || f.structure,
      // Pré-remplissage bailleur selon la structure
      ...(sci && !f.bailleur_sci ? {
        bailleur_sci:          sci.bailleur_sci,
        bailleur_forme:        sci.bailleur_forme,
        bailleur_siege:        sci.bailleur_siege,
        bailleur_gerants:      sci.bailleur_gerants,
        bailleur_capital:      sci.bailleur_capital,
        bailleur_rcs:          sci.bailleur_rcs,
        bailleur_rcs_numero:   sci.bailleur_rcs_numero,
        lieu_signature:        sci.lieu_signature_defaut || f.lieu_signature,
      } : sci ? {
        bailleur_sci:          f.bailleur_sci || sci.bailleur_sci,
        bailleur_forme:        f.bailleur_forme || sci.bailleur_forme,
        bailleur_siege:        f.bailleur_siege || sci.bailleur_siege,
        bailleur_gerants:      f.bailleur_gerants || sci.bailleur_gerants,
        bailleur_capital:      f.bailleur_capital || sci.bailleur_capital,
        bailleur_rcs:          f.bailleur_rcs || sci.bailleur_rcs,
        bailleur_rcs_numero:   f.bailleur_rcs_numero || sci.bailleur_rcs_numero,
        lieu_signature:        f.lieu_signature || sci.lieu_signature_defaut || "",
      } : {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = {
        ...formData,
        duree_ans: parseInt(formData.duree_ans),
        duree_preavis_mois: parseInt(formData.duree_preavis_mois) || 6,
        loyer_montant: parseFloat(formData.loyer_montant),
        depot_garantie: parseFloat(formData.depot_garantie),
        loyer_echeance_jour: parseInt(formData.loyer_echeance_jour),
        local_surface: formData.local_surface ? parseFloat(formData.local_surface) : null,
        local_surface_salle_attente: formData.local_surface_salle_attente ? parseFloat(formData.local_surface_salle_attente) : null,
        provision_charges: formData.provision_charges ? parseFloat(formData.provision_charges) : null,
        charges_incluses: [],
      };
      if (editMode) {
        await updateLease(editLeaseId, payload);
        toast.success("Bail mis à jour avec succès");
      } else {
        await createLease(payload);
        toast.success("Bail créé avec succès");
      }
      setDialogOpen(false);
      setFormData(EMPTY_FORM);
      setEditMode(false);
      setEditLeaseId(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setFormLoading(false);
    }
  };

  const openSignatureModal = (lease) => {
    setSignatureLinks(null);
    setSignatureLease(lease);
    setSignatureEmails({ bailleur_email: "", preneur_email: "" });
    setSignatureModal(true);
  };

  const handleSendSignature = async () => {
    if (!signatureLease) return;
    if (!signatureEmails.bailleur_email || !signatureEmails.preneur_email) {
      toast.error("Veuillez renseigner les deux emails");
      return;
    }
    setSendingSignature(true);
    try {
      const res = await sendLeaseForSignature(signatureLease.id, signatureEmails);
      setSignatureLinks({
        bailleur: res.data?.bailleur_link,
        preneur: res.data?.preneur_link,
      });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la génération des liens");
    } finally {
      setSendingSignature(false);
    }
  };

  const handleGenerateRiskReport = async (lease) => {
    setGeneratingRiskId(lease.id);
    try {
      const res = await generateLeaseRiskReport(lease.id);
      toast.success(res.data?.message || "État des risques ajouté aux annexes");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de générer l’état des risques");
    } finally {
      setGeneratingRiskId(null);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url).then(() => toast.success("Lien copié !"));
  };

  const openEditDialog = (lease) => {
    setFormData({
      tenant_id: lease.tenant_id || "",
      structure: lease.structure || "",
      bailleur_sci: lease.bailleur_sci || "",
      bailleur_forme: lease.bailleur_forme || "",
      bailleur_siege: lease.bailleur_siege || "",
      bailleur_gerants: lease.bailleur_gerants || "",
      bailleur_capital: lease.bailleur_capital || "",
      bailleur_rcs: lease.bailleur_rcs || "",
      bailleur_rcs_numero: lease.bailleur_rcs_numero || "",
      bailleur_contact: lease.bailleur_contact || "",
      locataire_civilite: lease.locataire_civilite || "Docteur",
      locataire_nom: lease.locataire_nom || "",
      locataire_profession: lease.locataire_profession || "",
      locataire_adresse: lease.locataire_adresse || "",
      locataire_date_naissance: lease.locataire_date_naissance || "",
      locataire_lieu_naissance: lease.locataire_lieu_naissance || "",
      locataire_rpps: lease.locataire_rpps || "",
      locataire_ordre: lease.locataire_ordre || "",
      local_adresse: lease.local_adresse || "",
      local_etage: lease.local_etage || "rez-de-chaussée",
      local_bureau_num: lease.local_bureau_num || "",
      local_surface: lease.local_surface != null ? String(lease.local_surface) : "",
      local_surface_salle_attente: lease.local_surface_salle_attente != null ? String(lease.local_surface_salle_attente) : "",
      local_sdo: lease.local_sdo || "",
      local_surface_totale_preneur: lease.local_surface_totale_preneur || "",
      local_parties_semi_communes: lease.local_parties_semi_communes || "",
      local_acces: lease.local_acces || "",
      cadastre_section: lease.cadastre_section || "",
      cadastre_numero: lease.cadastre_numero || "",
      cadastre_contenance: lease.cadastre_contenance || "",
      duree_ans: String(lease.duree_ans || 6),
      duree_preavis_mois: String(lease.duree_preavis_mois || 6),
      date_debut: lease.date_debut || "",
      loyer_montant: lease.loyer_montant != null ? String(lease.loyer_montant) : "",
      loyer_revision_ilat: lease.loyer_revision_ilat !== false,
      loyer_echeance_jour: String(lease.loyer_echeance_jour || 5),
      depot_garantie: lease.depot_garantie != null ? String(lease.depot_garantie) : "",
      provision_charges: lease.provision_charges != null ? String(lease.provision_charges) : "",
      charges_type: lease.charges_type || "provision",
      activite: lease.activite || "Consultations médicales",
      collaboration_autorisee: lease.collaboration_autorisee !== false,
      sous_location_autorisee: lease.sous_location_autorisee === true,
      date_diagnostic_amiante: lease.date_diagnostic_amiante || "",
      societe_diagnostic: lease.societe_diagnostic || "",
      societe_dpe: lease.societe_dpe || "",
      date_dpe: lease.date_dpe || "",
      lieu_signature: lease.lieu_signature || "",
      date_signature: lease.date_signature || "",
      notes: lease.notes || "",
    });
    setEditLeaseId(lease.id);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDownload = async (lease) => {
    setDownloadingId(lease.id);
    try {
      const res = await downloadLeasePdf(lease.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Bail_${lease.locataire_nom}_${lease.date_debut}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleOpenSign = (lease) => {
    // L'ouverture immédiate évite le blocage des popups sur téléphone.
    window.open("https://opensign-gt9j.srv1271606.hstgr.cloud/", "_blank", "noopener,noreferrer");
    handleDownload(lease);
    toast.success("Le bail est téléchargé. Importez-le maintenant dans OpenSign.");
  };

  const openSignDirectDialog = (lease) => {
    setSignDirectSigs({
      bailleur: lease.signature_bailleur || "",
      locataire: lease.signature_locataire || "",
    });
    setSignDirectDialog({ open: true, lease });
  };

  const handleSignDirect = async () => {
    if (!signDirectSigs.bailleur && !signDirectSigs.locataire) {
      toast.error("Veuillez ajouter au moins une signature");
      return;
    }
    setSignDirectLoading(true);
    try {
      await updateLease(signDirectDialog.lease.id, {
        signature_bailleur: signDirectSigs.bailleur || null,
        signature_locataire: signDirectSigs.locataire || null,
        status: (signDirectSigs.bailleur && signDirectSigs.locataire) ? "signé" : "brouillon",
      });
      toast.success("Signatures enregistrées — vous pouvez télécharger le PDF");
      setSignDirectDialog({ open: false, lease: null });
      fetchAll();
    } catch {
      toast.error("Erreur lors de l'enregistrement des signatures");
    } finally {
      setSignDirectLoading(false);
    }
  };

  const handleSendToNotion = async (lease) => {
    setSendingNotionId(lease.id);
    try {
      const res = await sendLeaseToNotion(lease.id);
      toast.success(res.data.message);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi sur Notion");
    } finally {
      setSendingNotionId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce bail ?")) return;
    try {
      await deleteLease(id);
      toast.success("Bail supprimé");
      fetchAll();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const filtered = leases.filter(l =>
    l.locataire_nom?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.local_adresse?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.bailleur_sci?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const set = (field) => (e) =>
    setFormData(f => ({ ...f, [field]: e.target ? e.target.value : e }));

  const sw = (field) => (v) => setFormData(f => ({ ...f, [field]: v }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Baux professionnels</h1>
          <p className="text-sm text-gray-500 mt-1">{leases.length} bail(s) enregistré(s)</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) { setEditMode(false); setEditLeaseId(null); setFormData(EMPTY_FORM); } }}>
          <DialogTrigger asChild>
            <Button><Plus className="w-4 h-4 mr-2" />Nouveau bail</Button>
          </DialogTrigger>
          <DialogContent className="w-full max-w-full sm:max-w-3xl h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-y-auto p-0 sm:p-6 rounded-none sm:rounded-lg">
            <DialogHeader className="sticky top-0 z-20 bg-white border-b px-4 py-3 sm:static sm:border-0 sm:p-0">
              <DialogTitle>{editMode ? "Modifier le bail" : "Créer un bail professionnel"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 px-4 pb-6 sm:px-0 sm:pb-0">
              <Tabs defaultValue="parties">
                <TabsList className="grid grid-cols-5 w-full overflow-x-auto">
                  <TabsTrigger value="parties">Parties</TabsTrigger>
                  <TabsTrigger value="local">Local</TabsTrigger>
                  <TabsTrigger value="loyer">Loyer</TabsTrigger>
                  <TabsTrigger value="clauses">Clauses</TabsTrigger>
                  <TabsTrigger value="diag">Diagnostics</TabsTrigger>
                </TabsList>

                {/* ── PARTIES ── */}
                <TabsContent value="parties" className="space-y-4 pt-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Locataire (Preneur)</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Locataire</Label>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span>Liste</span>
                        <Switch checked={freeTextMode} onCheckedChange={v => { setFreeTextMode(v); setFormData(f => ({...f, tenant_id: ""})); }} />
                        <span>Saisie libre (SCM…)</span>
                      </div>
                    </div>
                    {freeTextMode ? (
                      <Input value={formData.locataire_nom} onChange={set("locataire_nom")} placeholder="La SCM TRUC, La SELARL…" />
                    ) : (
                      <Select onValueChange={handleTenantChange} value={formData.tenant_id}>
                        <SelectTrigger><SelectValue placeholder="Sélectionner un locataire" /></SelectTrigger>
                        <SelectContent>
                          {tenants.sort((a,b)=>a.name.localeCompare(b.name)).map(t => (
                            <SelectItem key={t.id || t._id} value={t.id || t._id}>
                              {t.name}{t.structure ? " — " + t.structure : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Civilité / Titre (ou qualité)</Label>
                      <Input
                        value={formData.locataire_civilite}
                        onChange={set("locataire_civilite")}
                        placeholder="Docteur, M., La SCM, La SELARL..."
                        list="civilite-suggestions"
                      />
                      <datalist id="civilite-suggestions">
                        <option value="Docteur" />
                        <option value="Dr" />
                        <option value="Dr et Mme" />
                        <option value="M." />
                        <option value="Mme" />
                        <option value="M. et Mme" />
                        <option value="La SCM" />
                        <option value="La SELARL" />
                        <option value="La SAS" />
                        <option value="La SARL" />
                      </datalist>
                    </div>
                    <div>
                      <Label>Nom complet *</Label>
                      <Input value={formData.locataire_nom} onChange={set("locataire_nom")} required placeholder="DUPONT Jean" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Profession *</Label>
                      <Input value={formData.locataire_profession} onChange={set("locataire_profession")} placeholder="Médecin généraliste" required />
                    </div>
                    <div>
                      <Label>N° RPPS</Label>
                      <Input value={formData.locataire_rpps} onChange={set("locataire_rpps")} placeholder="10000000000" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Date de naissance</Label>
                      <Input value={formData.locataire_date_naissance} onChange={set("locataire_date_naissance")} placeholder="12/05/1980" />
                    </div>
                    <div>
                      <Label>Lieu de naissance</Label>
                      <Input value={formData.locataire_lieu_naissance} onChange={set("locataire_lieu_naissance")} placeholder="Lille" />
                    </div>
                  </div>
                  <div>
                    <Label>Inscription à l'Ordre professionnel</Label>
                    <Input value={formData.locataire_ordre} onChange={set("locataire_ordre")} placeholder="Conseil Départemental de l'Ordre des Médecins du Nord" />
                    <p className="text-xs text-slate-400 mt-1">Laisser vide si non applicable (psychologue, ostéopathe…)</p>
                  </div>
                  <div>
                    <Label>Adresse personnelle *</Label>
                    <Input value={formData.locataire_adresse} onChange={set("locataire_adresse")} required />
                  </div>

                  <hr className="my-2" />
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Bailleur</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label>Dénomination *</Label>
                      <Input value={formData.bailleur_sci} onChange={set("bailleur_sci")} placeholder="SCI C2GR ou BH MED" required />
                    </div>
                    <div>
                      <Label>Forme juridique</Label>
                      <Input value={formData.bailleur_forme} onChange={set("bailleur_forme")} placeholder="Société civile immobilière (SCI)" />
                    </div>
                    <div>
                      <Label>Capital social (€)</Label>
                      <Input value={formData.bailleur_capital} onChange={set("bailleur_capital")} placeholder="22 867,35" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label>Greffe / ville du RCS</Label>
                      <Input value={formData.bailleur_rcs} onChange={set("bailleur_rcs")} placeholder="Paris ou Lille Métropole" />
                    </div>
                    <div>
                      <Label>N° SIREN / RCS</Label>
                      <Input value={formData.bailleur_rcs_numero} onChange={set("bailleur_rcs_numero")} placeholder="999 671 316" />
                    </div>
                    <div>
                      <Label>Siège social *</Label>
                      <Input value={formData.bailleur_siege} onChange={set("bailleur_siege")} required />
                    </div>
                  </div>
                  <div>
                    <Label>Représenté par (gérants) *</Label>
                    <Input value={formData.bailleur_gerants} onChange={set("bailleur_gerants")} placeholder="M. DUPONT et Mme MARTIN" required />
                  </div>
                </TabsContent>

                {/* ── LOCAL ── */}
                <TabsContent value="local" className="space-y-4 pt-4">
                  <div>
                    <Label>Adresse du local *</Label>
                    <Input value={formData.local_adresse} onChange={set("local_adresse")} required />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Étage / Niveau</Label>
                      <Select onValueChange={set("local_etage")} value={formData.local_etage}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rez-de-chaussée">Rez-de-chaussée</SelectItem>
                          <SelectItem value="1er étage">1er étage</SelectItem>
                          <SelectItem value="2ème étage">2ème étage</SelectItem>
                          <SelectItem value="3ème étage">3ème étage</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>N° Bureau</Label>
                      <Input value={formData.local_bureau_num} onChange={set("local_bureau_num")} placeholder="12" />
                    </div>
                    <div>
                      <Label>Surface cabinet (m²)</Label>
                      <Input type="number" value={formData.local_surface} onChange={set("local_surface")} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Surface salle d'attente (m²)</Label>
                      <Input type="number" value={formData.local_surface_salle_attente} onChange={set("local_surface_salle_attente")} />
                    </div>
                    <div>
                      <Label>SDO total immeuble (m²)</Label>
                      <Input value={formData.local_sdo} onChange={set("local_sdo")} placeholder="ex: 450" />
                    </div>
                  </div>
                  <div>
                    <Label>Parties semi-communes</Label>
                    <Input
                      value={formData.local_parties_semi_communes}
                      onChange={set("local_parties_semi_communes")}
                      placeholder="Salle d'attente, sanitaires, couloir..."
                    />
                    <p className="text-xs text-slate-400 mt-1">Séparer les éléments par des virgules.</p>
                  </div>
                  <div>
                    <Label>Surface totale occupée par le preneur</Label>
                    <Input value={formData.local_surface_totale_preneur} onChange={set("local_surface_totale_preneur")} placeholder="ex: 18 m² cabinet + 4 m² salle d'attente" />
                  </div>
                  <hr className="my-1" />
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Cadastre</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Section</Label>
                      <Input value={formData.cadastre_section} onChange={set("cadastre_section")} placeholder="AB" />
                    </div>
                    <div>
                      <Label>N° parcelle</Label>
                      <Input value={formData.cadastre_numero} onChange={set("cadastre_numero")} placeholder="123" />
                    </div>
                    <div>
                      <Label>Contenance (m²)</Label>
                      <Input value={formData.cadastre_contenance} onChange={set("cadastre_contenance")} placeholder="850" />
                    </div>
                  </div>
                  <div>
                    <Label>Structure (SCI)</Label>
                    <Input value={formData.structure} onChange={set("structure")} placeholder="Seclin, Armentières..." />
                  </div>
                </TabsContent>

                {/* ── LOYER ── */}
                <TabsContent value="loyer" className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Date de début *</Label>
                      <Input type="date" value={formData.date_debut} onChange={set("date_debut")} required />
                    </div>
                    <div>
                      <Label>Durée du bail</Label>
                      <Select onValueChange={set("duree_ans")} value={formData.duree_ans}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[1,2,3,4,5,6,9].map(n => (
                            <SelectItem key={n} value={String(n)}>{n} an{n>1?"s":""}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Loyer mensuel HC (€) *</Label>
                      <Input type="number" value={formData.loyer_montant} onChange={set("loyer_montant")} required />
                      {formData.loyer_montant && (
                        <p className="text-xs text-slate-400 mt-1">
                          Annuel : {(parseFloat(formData.loyer_montant) * 12).toLocaleString("fr-FR")} €
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>Dépôt de garantie (€) *</Label>
                      <Input type="number" value={formData.depot_garantie} onChange={set("depot_garantie")} required />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Provision mensuelle charges (€)</Label>
                      <Input type="number" value={formData.provision_charges} onChange={set("provision_charges")} placeholder="0" />
                    </div>
                    <div>
                      <Label>Échéance paiement (jour du mois)</Label>
                      <Input type="number" min="1" max="28" value={formData.loyer_echeance_jour} onChange={set("loyer_echeance_jour")} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                  <div className="space-y-2 pt-2">
                    <Label className="text-sm font-medium">Type de charges</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData(f => ({ ...f, charges_type: "provision" }))}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${formData.charges_type === "provision" ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-gray-300"}`}
                      >
                        <div className="font-medium text-sm">Provision + régularisation</div>
                        <div className="text-xs text-gray-500 mt-0.5">Appel mensuel + ajustement annuel</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(f => ({ ...f, charges_type: "fixes" }))}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${formData.charges_type === "fixes" ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-gray-300"}`}
                      >
                        <div className="font-medium text-sm">Charges forfaitaires fixes</div>
                        <div className="text-xs text-gray-500 mt-0.5">Montant fixe, sans régularisation</div>
                      </button>
                    </div>
                    {formData.charges_type === "fixes" && (
                      <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
                        Le PDF mentionnera un forfait définitif sans régularisation annuelle.
                      </p>
                    )}
                  </div>
                    <Switch checked={formData.loyer_revision_ilat} onCheckedChange={sw("loyer_revision_ilat")} />
                    <Label>Révision annuelle ILAT</Label>
                  </div>
                </TabsContent>

                {/* ── CLAUSES ── */}
                <TabsContent value="clauses" className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Préavis de départ (mois)</Label>
                      <Select onValueChange={set("duree_preavis_mois")} value={formData.duree_preavis_mois}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[1,2,3,6,9,12].map(n => (
                            <SelectItem key={n} value={String(n)}>{n} mois</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-400 mt-1">Délai légal minimum : 6 mois pour un bail professionnel</p>
                    </div>
                    <div className="flex items-end pb-6">
                      <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
                        Ce délai s'applique au préavis de départ (bailleur et locataire)
                      </p>
                    </div>
                  </div>
                  <div>
                    <Label>Activité exercée *</Label>
                    <Input value={formData.activite} onChange={set("activite")} placeholder="Consultations médicales" required />
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Switch checked={formData.collaboration_autorisee} onCheckedChange={sw("collaboration_autorisee")} />
                      <div>
                        <Label>Collaboration / remplacement autorisé</Label>
                        <p className="text-xs text-slate-400">Mise à disposition à un collaborateur libéral (loi DUTREIL)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={formData.sous_location_autorisee} onCheckedChange={sw("sous_location_autorisee")} />
                      <div>
                        <Label>Sous-location autorisée</Label>
                        <p className="text-xs text-slate-400">Par défaut interdite sauf accord exprès</p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Lieu de signature</Label>
                      <Input value={formData.lieu_signature} onChange={set("lieu_signature")} placeholder="Seclin" />
                    </div>
                    <div>
                      <Label>Date de signature</Label>
                      <Input type="date" value={formData.date_signature} onChange={set("date_signature")} />
                    </div>
                  </div>
                  <div>
                    <Label>Dispositions particulières</Label>
                    <Textarea value={formData.notes} onChange={set("notes")} rows={4} placeholder="Clauses spécifiques..." />
                  </div>
                </TabsContent>

                {/* ── DIAGNOSTICS ── */}
                <TabsContent value="diag" className="space-y-4 pt-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Diagnostic Amiante</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Date du diagnostic amiante</Label>
                      <Input type="date" value={formData.date_diagnostic_amiante} onChange={set("date_diagnostic_amiante")} />
                    </div>
                    <div>
                      <Label>Société ayant réalisé le diagnostic</Label>
                      <Input value={formData.societe_diagnostic} onChange={set("societe_diagnostic")} placeholder="DIAGIMMO NORD" />
                    </div>
                  </div>
                  <hr className="my-1" />
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">DPE — Diagnostic de Performance Énergétique</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Société ayant réalisé le DPE</Label>
                      <Input value={formData.societe_dpe} onChange={set("societe_dpe")} placeholder="DIAGIMMO NORD" />
                    </div>
                    <div>
                      <Label>Date du DPE</Label>
                      <Input type="date" value={formData.date_dpe} onChange={set("date_dpe")} />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                <Button type="submit" disabled={formLoading}>
                  {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editMode ? "Enregistrer les modifications" : "Créer le bail"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          className="pl-9"
          placeholder="Rechercher un bail..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ScrollText className="w-5 h-5" /> Baux enregistrés
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>Aucun bail enregistré</p>
              <p className="text-sm mt-1">Créez votre premier bail avec le bouton ci-dessus</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Locataire</TableHead>
                  <TableHead>Local</TableHead>
                  <TableHead>SCI</TableHead>
                  <TableHead>Début</TableHead>
                  <TableHead>Durée</TableHead>
                  <TableHead>Loyer</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(lease => (
                  <TableRow key={lease.id}>
                    <TableCell className="font-medium">
                      {lease.locataire_civilite} {lease.locataire_nom}
                      <div className="text-xs text-gray-400">{lease.locataire_profession}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{lease.local_adresse}</div>
                      {lease.local_bureau_num && <div className="text-xs text-gray-400">Bureau {lease.local_bureau_num}</div>}
                    </TableCell>
                    <TableCell><Badge variant="outline">{lease.bailleur_sci}</Badge></TableCell>
                    <TableCell className="text-sm">{lease.date_debut}</TableCell>
                    <TableCell className="text-sm">{lease.duree_ans} ans</TableCell>
                    <TableCell>
                      <div className="font-semibold">{lease.loyer_montant?.toLocaleString("fr-FR")} €</div>
                      {lease.provision_charges > 0 && (
                        <div className="text-xs text-gray-400">
                          + {lease.provision_charges?.toLocaleString("fr-FR")} € {lease.charges_type === "fixes" ? "forfait" : "prov."}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge variant={lease.status === "signé" ? "default" : "secondary"}>
                          {lease.status}
                        </Badge>
                        {lease.signature_status === "en_attente" && (
                          <Badge variant="outline" className="text-indigo-600 border-indigo-300 text-xs">
                            ✍️ En attente sig.
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        <Button size="sm" variant="outline"
                          onClick={() => handleDownload(lease)}
                          disabled={downloadingId === lease.id}
                          title="Télécharger PDF">
                          {downloadingId === lease.id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <Download className="w-4 h-4" />}
                        </Button>
                        <Button size="sm" variant="outline"
                          className="text-amber-600 hover:text-amber-700"
                          onClick={() => handleGenerateRiskReport(lease)}
                          disabled={generatingRiskId === lease.id}
                          title="Générer ou actualiser l’état des risques">
                          {generatingRiskId === lease.id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <ShieldCheck className="w-4 h-4" />}
                        </Button>
                        <Button size="sm" variant="outline"
                          className="text-blue-600 hover:text-blue-700"
                          onClick={() => openEditDialog(lease)}
                          title="Modifier le bail">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline"
                          className={lease.signature_bailleur && lease.signature_locataire
                            ? "text-green-600 hover:text-green-700 border-green-300"
                            : "text-violet-600 hover:text-violet-700"}
                          onClick={() => openSignDirectDialog(lease)}
                          title="Signer en direct sur le téléphone">
                          {lease.signature_bailleur && lease.signature_locataire
                            ? <CheckCircle2 className="w-4 h-4" />
                            : <PenLine className="w-4 h-4" />}
                        </Button>
                        <Button size="sm" variant="outline"
                          className="text-indigo-600 hover:text-indigo-700"
                          onClick={() => handleOpenSign(lease)}
                          title="Télécharger le bail et l’ouvrir dans OpenSign">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline"
                          className={lease.sent_to_notion_at ? "text-emerald-600 hover:text-emerald-700" : "text-slate-600 hover:text-slate-700"}
                          onClick={() => handleSendToNotion(lease)}
                          disabled={sendingNotionId === lease.id}
                          title={lease.sent_to_notion_at
                            ? `Déjà envoyé sur Notion le ${new Date(lease.sent_to_notion_at).toLocaleDateString("fr-FR")} — cliquer pour renvoyer`
                            : "Envoyer le bail (PDF) sur la fiche Notion du locataire"}>
                          {sendingNotionId === lease.id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <BookOpen className="w-4 h-4" />}
                        </Button>
                        <Button size="sm" variant="outline"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(lease.id)}
                          title="Supprimer">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {/* Modal envoi pour signature */}
      <Dialog open={signatureModal} onOpenChange={setSignatureModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="w-5 h-5 text-indigo-600" />
              Envoyer pour signature électronique
            </DialogTitle>
          </DialogHeader>
          {signatureLease && (
            <div className="space-y-4 pt-2">
              {!signatureLinks ? (
                <>
                  <p className="text-sm text-gray-600">
                    Bail de <strong>{signatureLease.locataire_nom}</strong> — {signatureLease.local_adresse}
                  </p>
                  <div className="space-y-2">
                    <Label>Email du bailleur</Label>
                    <Input
                      type="email"
                      placeholder="bailleur@example.com"
                      value={signatureEmails.bailleur_email}
                      onChange={e => setSignatureEmails(p => ({ ...p, bailleur_email: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email du preneur (locataire)</Label>
                    <Input
                      type="email"
                      placeholder="docteur@cabinet.fr"
                      value={signatureEmails.preneur_email}
                      onChange={e => setSignatureEmails(p => ({ ...p, preneur_email: e.target.value }))}
                    />
                  </div>
                  <p className="text-xs text-gray-400">
                    Un lien de signature unique sera généré pour chaque partie. Partagez-le par email, SMS ou WhatsApp.
                  </p>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-green-600 font-medium">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    Liens de signature générés !
                  </div>
                  <div className="space-y-3">
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <p className="text-xs font-semibold text-gray-500 mb-1">🏢 BAILLEUR</p>
                      <div className="flex items-center gap-2">
                        <input readOnly value={signatureLinks.bailleur || ""} className="flex-1 text-xs bg-white border rounded px-2 py-1 text-gray-700 truncate" />
                        <button onClick={() => copyLink(signatureLinks.bailleur)} className="shrink-0 px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700">Copier</button>
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <p className="text-xs font-semibold text-gray-500 mb-1">🩺 PRENEUR</p>
                      <div className="flex items-center gap-2">
                        <input readOnly value={signatureLinks.preneur || ""} className="flex-1 text-xs bg-white border rounded px-2 py-1 text-gray-700 truncate" />
                        <button onClick={() => copyLink(signatureLinks.preneur)} className="shrink-0 px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700">Copier</button>
                      </div>
                    </div>
                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
                      Envoyez chaque lien à la bonne personne par email, SMS ou WhatsApp. La signature se fait directement dans le navigateur.
                    </p>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setSignatureModal(false); setSignatureLinks(null); setSignatureEmails({ bailleur_email: "", preneur_email: "" }); }}>
                  {signatureLinks ? "Fermer" : "Annuler"}
                </Button>
                {!signatureLinks && (
                  <Button
                    onClick={handleSendSignature}
                    disabled={sendingSignature}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    {sendingSignature
                      ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Génération...</>
                      : <><Send className="w-4 h-4 mr-2" />Générer les liens</>}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Dialog signature directe sur place ── */}
      <Dialog open={signDirectDialog.open} onOpenChange={open => setSignDirectDialog(s => ({ ...s, open }))}>
        <DialogContent className="w-full max-w-full sm:max-w-lg h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-y-auto p-0 sm:p-6 rounded-none sm:rounded-lg">
          <div className="sticky top-0 bg-white border-b sm:border-none px-4 py-3 sm:p-0 z-10">
            <DialogHeader>
              <DialogTitle className="text-base flex items-center gap-2">
                <PenLine className="w-4 h-4 text-violet-600" />
                Signature sur place — {signDirectDialog.lease?.locataire_nom}
              </DialogTitle>
            </DialogHeader>
          </div>

          <div className="space-y-5 px-4 pb-6 sm:px-0 sm:pb-0 mt-4 sm:mt-0">
            <p className="text-xs text-gray-500 bg-violet-50 border border-violet-100 rounded-lg p-3">
              Faites signer chaque partie directement sur l'écran. Une fois validé, téléchargez le PDF — les signatures y seront intégrées.
            </p>

            <SignaturePad
              label="Signature du bailleur"
              value={signDirectSigs.bailleur}
              onChange={v => setSignDirectSigs(s => ({ ...s, bailleur: v }))}
            />
            <SignaturePad
              label="Signature du locataire (preneur)"
              value={signDirectSigs.locataire}
              onChange={v => setSignDirectSigs(s => ({ ...s, locataire: v }))}
            />

            <div className="flex gap-2 pt-2 sticky bottom-0 bg-white pb-2 sm:static sm:pb-0">
              <Button
                variant="outline"
                className="flex-1 sm:flex-none"
                onClick={() => setSignDirectDialog({ open: false, lease: null })}
              >
                Annuler
              </Button>
              <Button
                className="flex-1 bg-violet-600 hover:bg-violet-700"
                onClick={handleSignDirect}
                disabled={signDirectLoading || (!signDirectSigs.bailleur && !signDirectSigs.locataire)}
              >
                {signDirectLoading
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enregistrement...</>
                  : <><CheckCircle2 className="w-4 h-4 mr-2" />Valider les signatures</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
